import React, { useEffect, useState } from 'react';
import Page, { usePage } from '../../components/Page';
import { Button, Grid, TextField } from '@mui/material';
import { SideBar, useSideBar } from '../../components/SideBar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { api } from '../../api';
import { faBars, faMagnifyingGlass, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { useStyles } from '../../hooks/useStyles';
import { DataGrid, GridColDef, GridColumnHeaderParams, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid';
import { EditStoreDialog, useEditStoreDialog } from './EditStoreDialog';
import { useAuth } from '../../units/auth/AuthProvider';
import { getMaxAuthority } from '../../api/user-group';
import { grey } from '@mui/material/colors';
import { Store } from '../../api/store';

export const useStoreManagePage = () => {
    const page = usePage();
    const classes = useStyles();
    const auth = useAuth();
    const [stores, setStores] = useState<Store[]>([]);
    const reloadStores = async () => {
        api.store
            .get({})
            .then((ret) => ret.success && setStores(ret.data?.sort((a, b) => (a.storeName || '').localeCompare(b.storeName || '')) || []));
    };
    const sideBar = useSideBar();
    const editStoreDialog = useEditStoreDialog();
    const [inputSearchConfig, setInputSearchConfig] = useState('');
    const columns: GridColDef[] = [
        { field: 'storeId', headerName: '店面ID', flex: 4, minWidth: 120, cellClassName: 'cell', hide: true },
        { field: 'storeName', headerName: '店面名稱', flex: 4, minWidth: 120, cellClassName: 'cell', hide: false },
        {
            field: 'action',
            renderHeader: (params: GridColumnHeaderParams) => <>{'操作'}</>,
            width: 180,
            cellClassName: 'cell',
            renderCell: (params: GridRenderCellParams) => {
                const row = params.row as Store;
                return (
                    <Grid container justifyContent={'space-around'}>
                        <Button
                            variant='contained'
                            className={classes.buttonWhite}
                            onClick={() => {
                                editStoreDialog.setStoreId(row.storeId || '');
                                editStoreDialog.setInputStoreName(row.storeName || '');
                                editStoreDialog.setOnUpdateStore(() => (storeId: string, storeName: string) => {
                                    api.store.update({ storeId: storeId, storeName: storeName }).then((ret) => {
                                        if (ret.success) {
                                            reloadStores();
                                            page.simpleAlert.show({
                                                autoHideDuration: 2000,
                                                severity: 'success',
                                                message: ret.message,
                                            });
                                            editStoreDialog.onClose();
                                        } else {
                                            page.simpleAlert.show({
                                                autoHideDuration: 6000,
                                                severity: 'error',
                                                message: JSON.stringify(ret),
                                            });
                                            editStoreDialog.onClose();
                                        }
                                    });
                                });
                                editStoreDialog.onOpen();
                            }}
                        >
                            {'編輯'}
                        </Button>
                        <Button
                            variant='contained'
                            className={classes.buttonRed}
                            onClick={() =>
                                page.alert.show({
                                    type: 'warning',
                                    title: '警告',
                                    message: '確定要刪除？',
                                    onConfirm: async () => {
                                        page.setIsLoading(true);
                                        const ret = await api.store
                                            .remove({ storeId: row.storeId! })
                                            .finally(() => page.setIsLoading(false));
                                        if (ret.success) {
                                            reloadStores();
                                            page.simpleAlert.show({ autoHideDuration: 2000, severity: 'success', message: ret.message });
                                        } else {
                                            page.simpleAlert.show({
                                                autoHideDuration: 6000,
                                                severity: 'error',
                                                message: JSON.stringify(ret),
                                            });
                                        }
                                    },
                                })
                            }
                        >
                            {'刪除'}
                        </Button>
                    </Grid>
                );
            },
        },
    ];
    useEffect(() => {
        reloadStores();
    }, []);
    return {
        page,
        classes,
        auth,
        stores,
        setStores,
        reloadStores,
        sideBar,
        editStoreDialog,
        inputSearchConfig,
        setInputSearchConfig,
        columns,
    };
};

export const StoreManagePage = (): React.ReactElement => {
    const props = useStoreManagePage();
    return (
        <Page {...props.page} title='店面管理'>
            <Grid container flexDirection={'row'} height={'100%'}>
                <Grid container item flex={1} flexDirection={'column'} padding={4} sx={{ backgroundColor: grey[100] }}>
                    <Grid container item flexDirection={'row'}>
                        <Grid container item flexDirection={'row'} justifyContent={'flex-end'} xs={12}>
                            <TextField
                                value={props.inputSearchConfig}
                                onChange={(e) => props.setInputSearchConfig(e.target.value)}
                                variant='outlined'
                                label={
                                    <>
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                        {`搜尋(${['店名'].join(', ')})`}
                                    </>
                                }
                                style={{ flex: 1, width: '100%', margin: 4, marginTop: 12, backgroundColor: 'white' }}
                            />
                            <Button
                                className={props.classes.buttonCyan}
                                style={{ margin: 4, marginTop: 12 }}
                                onClick={() => {
                                    props.editStoreDialog.setOnCreateStore(() => (storeName: string) => {
                                        api.store.create({ storeName: storeName }).then((ret) => {
                                            if (ret.success) {
                                                props.reloadStores();
                                                props.page.simpleAlert.show({
                                                    autoHideDuration: 2000,
                                                    severity: 'success',
                                                    message: ret.message,
                                                });
                                                props.editStoreDialog.onClose();
                                            } else {
                                                props.page.simpleAlert.show({
                                                    autoHideDuration: 6000,
                                                    severity: 'error',
                                                    message: JSON.stringify(ret),
                                                });
                                                props.editStoreDialog.onClose();
                                            }
                                        });
                                    });
                                    props.editStoreDialog.onOpen();
                                }}
                            >
                                <FontAwesomeIcon icon={faPlus} fixedWidth />
                                {'新增店面'}
                            </Button>
                        </Grid>
                    </Grid>
                    <Grid container item flex={1} padding={2} borderRadius={2} marginTop={2} sx={{ backgroundColor: 'white' }}>
                        <DataGrid
                            rows={(() => {
                                const searchs = props.inputSearchConfig.split(/\s+/).filter((v) => v.length > 0);
                                return props.stores.filter((v, i, a) =>
                                    [
                                        searchs.length == 0
                                            ? true
                                            : searchs
                                                  .map((s) => v.storeName?.toUpperCase().includes(s.toUpperCase()) || false)
                                                  .every((v) => v),
                                    ].every((v) => v),
                                );
                            })()}
                            columns={props.columns}
                            getRowId={(row: Store) => row.storeId!}
                            pageSize={10}
                            rowsPerPageOptions={[10]}
                            experimentalFeatures={{ newEditingApi: true }}
                            disableColumnMenu={true}
                            disableSelectionOnClick={true}
                        />
                    </Grid>
                </Grid>
            </Grid>
            <EditStoreDialog {...props.editStoreDialog} />
        </Page>
    );
};
